import { updateCustomerById } from "../../../app/api/updateCustomerById";
import { DEFAULT_SEATING_TYPE } from "../../../Components/Common/Inputs/SeatingInput";
import { BaseAction } from "../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IUserResponse } from "../../../Orchestrator/Types/Index";
import { invalidateUserCaches } from "../MyAccount/invalidateUserCaches";

interface ISaveUserInformationActionRequest {
  userData: IUserResponse;
  updatedUserData?: Partial<IUserResponse>;
}

class SaveUserInformationAction extends BaseAction<IUserResponse | null, ISaveUserInformationActionRequest> {
  override async run(): Promise<IUserResponse | null> {
    const { userData, updatedUserData } = this.params || {};

    let updatedUserResponse: IUserResponse | null = null;

    if (!(userData && updatedUserData)) {
      console.error("User data not provided.");
      return null;
    }

    try {
      let payload: Partial<IUserResponse> = {
        ...(userData || {}),
        ...updatedUserData,
      }

      if ("railPreferences" in updatedUserData && updatedUserData.railPreferences) {
        let railPreferencePayload: Partial<IUserResponse["railPreferences"]> = {
          ...(userData.railPreferences || {}),
          ...updatedUserData.railPreferences,
        };

        if ("preferredOrigin" in updatedUserData.railPreferences) {
          railPreferencePayload.preferredOrigin = updatedUserData.railPreferences.preferredOrigin;
        }
        if ("preferredDestination" in updatedUserData.railPreferences) {
          railPreferencePayload.preferredDestination = updatedUserData.railPreferences.preferredDestination;
        }
        if ("classType" in updatedUserData.railPreferences) {
          railPreferencePayload.classType = updatedUserData.railPreferences.classType;
        }
        if ("ticketOption" in updatedUserData.railPreferences) {
          railPreferencePayload.ticketOption = updatedUserData.railPreferences.ticketOption;
        }
        if (railPreferencePayload.seating?.position || railPreferencePayload.seating?.type) {
          railPreferencePayload.seating = {
            position: railPreferencePayload.seating.position || (railPreferencePayload.seating?.position ?? ""),
            type: railPreferencePayload.seating.type || (railPreferencePayload.seating?.type ?? "") || DEFAULT_SEATING_TYPE,
          };
        }
        if ("meal" in updatedUserData.railPreferences) {
          railPreferencePayload.meal = updatedUserData.railPreferences.meal;
        }

        if (!userData?.railPreferences?.multiBooking?.preferredOrigin && railPreferencePayload?.preferredOrigin) {
          railPreferencePayload.multiBooking = {
            ...(userData?.railPreferences?.multiBooking || {}),
            preferredOrigin: railPreferencePayload.preferredOrigin
          }
        }

        // Limit 2 favorites for each type
        const MAX_FAVORITE_LOCATIONS = 2;
        let favoriteDepartures = payload.railPreferences?.favoriteDepartures || userData?.railPreferences?.favoriteDepartures || []
        let favoriteDeparturesOrigin = (favoriteDepartures?.filter(loc => "origin" in loc) || []).slice(0, MAX_FAVORITE_LOCATIONS);
        let favoriteDeparturesDestination = (favoriteDepartures?.filter(loc => "destination" in loc) || []).slice(0, MAX_FAVORITE_LOCATIONS);
        payload.railPreferences = {
          ...payload.railPreferences,
          favoriteDepartures: [...favoriteDeparturesOrigin, ...favoriteDeparturesDestination]
        }

        if (!userData?.railPreferences?.multiBooking?.preferredDestination && railPreferencePayload?.preferredDestination) {
          railPreferencePayload.multiBooking = {
            ...(userData?.railPreferences?.multiBooking || {}),
            preferredDestination: railPreferencePayload.preferredDestination
          };
        }

        payload.railPreferences = railPreferencePayload;
      }

      const response = await updateCustomerById(userData.id.toString(), payload);

      if (response.status === "ok" && response.answer) {
        updateRecoilState("userDataAtom", response.answer);
        invalidateUserCaches();
        updatedUserResponse = response.answer;
      } else {
        console.warn("Failed to save user information.");
      }

    } catch (error) {
      console.error("Error saving user information:", error);
    } finally {
      return updatedUserResponse;
    }
  }
}

export const saveUserInformationAction = new SaveUserInformationAction();